<template>
  <div class="st-configuration-form">
    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
      <div class="text-caption grey--text mt-2">{{ $t('configurationForm.loading') }}</div>
    </div>

    <!-- Load failed -->
    <div v-else-if="loadFailed && !valuesLoaded" class="text-center py-8">
      <v-icon large color="error">mdi-alert-circle-outline</v-icon>
      <div class="text-caption error--text mt-2">{{ $t('configurationForm.loadFailed') }}</div>
      <v-btn small outlined class="mt-3" @click="fetchData">
        {{ $t('configurationForm.retry') }}
      </v-btn>
    </div>

    <!-- Empty state -->
    <div v-else-if="configGroups.length === 0" class="text-center py-8">
      <v-icon large color="grey">mdi-cog-off-outline</v-icon>
      <div class="text-caption grey--text mt-2">{{ $t('configurationForm.noSettings') }}</div>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Category tabs (pinned top) -->
      <v-tabs v-if="configGroups.length > 1" v-model="activeTab" dense>
        <v-tab
          v-for="group in configGroups"
          :key="group.category.id"
        >
          {{ group.category.name }}
          <v-tooltip v-if="group.category.description" bottom>
            <template #activator="{ on, attrs }">
              <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>{{ group.category.description }}</span>
          </v-tooltip>
        </v-tab>
      </v-tabs>

      <v-alert v-if="loadFailed" type="warning" dense outlined class="mt-2 mb-0">
        <div class="d-flex align-center">
          <span class="text-caption">{{ $t('configurationForm.refreshFailed') }}</span>
          <v-spacer />
          <v-btn small text @click="refreshValues">{{ $t('configurationForm.retry') }}</v-btn>
        </div>
      </v-alert>

      <!-- Sidebar + scrollable content -->
      <div class="st-configuration-form__body mt-2">
        <!-- Sidebar: pinned sub-category navigation -->
        <v-card
          v-if="activeSections.length > 1"
          outlined
          class="st-configuration-form__sidebar"
        >
          <v-list dense nav>
            <v-list-item
              v-for="(section, idx) in activeSections"
              :key="section.subCategory.id"
              :input-value="activeSection === idx"
              color="primary"
              @click="activeSection = idx"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ section.subCategory.name }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="section.subCategory.description" class="my-0">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon x-small v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                  </template>
                  <span>{{ section.subCategory.description }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Content: scrollable fields area -->
        <div class="st-configuration-form__content">
          <STConfigurationSection
            v-if="activeSections[activeSection]"
            :key="activeSections[activeSection].subCategory.id"
            :title="activeSections[activeSection].subCategory.name"
            :description="activeSections[activeSection].subCategory.description"
            :fields="activeSections[activeSection].fields"
            :hide-title="activeSections.length > 1"
            :hotel-id="hotelId"
            :readonly="readonly"
            @save="onSaveField"
            @clear-override="onClearOverride"
          />
          <div v-else class="text-center py-8 grey--text text-caption">
            {{ $t('configurationForm.selectSection') }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import STConfigurationSection from "./STConfigurationSection.vue";

interface Props {
  hotelId: number | string;
  categoryCodes?: string[];
  scopeType?: string;
  scopeId?: number;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  categoryCodes: () => [],
  scopeType: "hotel",
  scopeId: 0,
  readonly: false,
});

const emit = defineEmits<{
  (e: "saved", payload: { configurationId: number; value: string }): void;
  (e: "error", payload: { message: string }): void;
}>();

const instance = getCurrentInstance();
const http = instance?.proxy?.$http;

const loading = ref(false);
const activeTab = ref(0);
const activeSection = ref(0);
const categories = ref<any[]>([]);
const subCategories = ref<any[]>([]);
const headers = ref<any[]>([]);
const values = ref<any[]>([]);
const loadFailed = ref(false);
const valuesLoaded = ref(false);

const baseUrl = computed(() => `/hotel/${props.hotelId}/dto`);

const scopeRequiresId = (scopeType: string) =>
  scopeType === "chain" || scopeType === "brand" || scopeType === "outlet";

let fetchGeneration = 0;
const isStale = (generation: number) => generation !== fetchGeneration;

const errorMessage = (err: any, fallback: string) =>
  err?.response?.data?.Message || err?.message || fallback;

const loadFailedText = () =>
  String(instance?.proxy?.$t?.("configurationForm.loadFailed") || "Failed to load configuration");

const saveFailedText = () =>
  String(instance?.proxy?.$t?.("configurationForm.saveFailed") || "Failed to save configuration");

const fetchEffectiveValues = async (generation: number) => {
  if (!http || headers.value.length === 0) {
    if (isStale(generation)) return false;
    values.value = [];
    return true;
  }
  const params = [`scope_type=${encodeURIComponent(props.scopeType)}`];
  if (scopeRequiresId(props.scopeType)) {
    params.push(`scope_id=${props.scopeId}`);
  }
  const res = await http.get(
    `/hotel/${props.hotelId}/configuration/values/effective/v1?${params.join("&")}`
  );
  if (isStale(generation)) return false;
  values.value = res?.data?.Data || [];
  return true;
};

const refreshValues = async () => {
  const generation = fetchGeneration;
  try {
    if (!(await fetchEffectiveValues(generation))) return;
    loadFailed.value = false;
    valuesLoaded.value = true;
  } catch (err: any) {
    if (isStale(generation)) return;
    loadFailed.value = true;
    emit("error", { message: errorMessage(err, loadFailedText()) });
  }
};

const fetchData = async () => {
  if (!http) return;
  const generation = ++fetchGeneration;
  loading.value = true;
  activeSection.value = 0;
  loadFailed.value = false;
  valuesLoaded.value = false;
  try {
    // Build category filter
    let categoryUrl = `${baseUrl.value}/general.configuration_category/v1?sort_by[]=sort_order&sort_by_desc[]=false&page_size=500`;
    if (props.categoryCodes.length > 0) {
      categoryUrl += `&filter_by[]=code&filter_by_operator[]=7&filter_by_value[]=${props.categoryCodes.join(",")}`;
    }

    // Fetch categories
    const catRes = await http.get(categoryUrl);
    if (isStale(generation)) return;
    categories.value = catRes?.data?.Data || [];

    if (categories.value.length === 0) return;

    // Fetch sub categories
    const catIds = categories.value.map((c: any) => c.id).join(",");
    const subCatUrl = `${baseUrl.value}/general.configuration_sub_category/v1?filter_by[]=configuration_category_id&filter_by_operator[]=7&filter_by_value[]=${catIds}&sort_by[]=sort_order&sort_by_desc[]=false&page_size=500`;
    const subCatRes = await http.get(subCatUrl);
    if (isStale(generation)) return;
    subCategories.value = subCatRes?.data?.Data || [];

    // Fetch headers
    if (subCategories.value.length > 0) {
      const subCatIds = subCategories.value.map((sc: any) => sc.id).join(",");
      const headerUrl = `${baseUrl.value}/general.configuration_header/v1?filter_by[]=configuration_sub_category_id&filter_by_operator[]=7&filter_by_value[]=${subCatIds}&sort_by[]=sort_order&sort_by_desc[]=false&page_size=500`;
      const headerRes = await http.get(headerUrl);
      if (isStale(generation)) return;
      headers.value = headerRes?.data?.Data || [];
    }

    if (!(await fetchEffectiveValues(generation))) return;
    valuesLoaded.value = true;
  } catch (err: any) {
    if (isStale(generation)) return;
    loadFailed.value = true;
    emit("error", { message: errorMessage(err, loadFailedText()) });
  } finally {
    if (!isStale(generation)) loading.value = false;
  }
};

const effectiveByCode = computed(() => {
  const map: Record<string, any> = {};
  for (const v of values.value) {
    if (v?.code) map[v.code] = v;
  }
  return map;
});

// Build grouped structure
const configGroups = computed(() => {
  const subCatMap = new Map<number, any[]>();
  for (const sc of subCategories.value) {
    if (!subCatMap.has(sc.configuration_category_id)) {
      subCatMap.set(sc.configuration_category_id, []);
    }
    subCatMap.get(sc.configuration_category_id)!.push(sc);
  }

  const headerMap = new Map<number, any[]>();
  for (const h of headers.value) {
    if (!headerMap.has(h.configuration_sub_category_id)) {
      headerMap.set(h.configuration_sub_category_id, []);
    }
    headerMap.get(h.configuration_sub_category_id)!.push(h);
  }

  const writeScopeId = scopeRequiresId(props.scopeType) ? (props.scopeId ?? 0) : 0;

  return categories.value.map((cat: any) => {
    const catSubCats = subCatMap.get(cat.id) || [];
    return {
      category: cat,
      sections: catSubCats.map((sc: any) => {
        const scHeaders = headerMap.get(sc.id) || [];
        return {
          subCategory: sc,
          fields: scHeaders.map((h: any) => {
            const effective = effectiveByCode.value[h.code];
            return {
              header: h,
              value: effective?.own_value ?? effective?.effective_value ?? h.default_value ?? "",
              valueId: 0,
              scopeType: props.scopeType,
              scopeId: writeScopeId,
              effective,
            };
          }),
        };
      }),
    };
  });
});

// Sections for the currently active tab
const activeSections = computed(() => {
  const group = configGroups.value[activeTab.value];
  return group ? group.sections : [];
});

// Reset active section when switching tabs
watch(activeTab, () => {
  activeSection.value = 0;
});

const onSaveField = async (payload: any) => {
  if (!http) return;
  try {
    const url = `/hotel/${props.hotelId}/configuration/submit/set-value/v1`;
    const body: Record<string, any> = {
      configuration_id: payload.configuration_id,
      scope_type: payload.scope_type,
      value: payload.value,
    };
    if (scopeRequiresId(payload.scope_type)) {
      body.scope_id = payload.scope_id;
    }
    await http.post(url, body);

    // Only the server knows the resolved value and its source after a write.
    await refreshValues();

    emit("saved", { configurationId: payload.configuration_id, value: payload.value });

    instance?.proxy?.$archNotify?.({
      color: "success",
      message: String(instance?.proxy?.$t?.("configurationForm.saved") || "Configuration saved"),
    });
  } catch (err: any) {
    instance?.proxy?.$archNotify?.({
      color: "error",
      message: errorMessage(err, saveFailedText()),
    });
    emit("error", { message: errorMessage(err, "Failed to save") });
  }
};

const onClearOverride = async (payload: any) => {
  if (!http) return;
  try {
    const body: Record<string, any> = {
      configuration_id: payload.configuration_id,
      scope_type: payload.scope_type,
    };
    if (scopeRequiresId(payload.scope_type)) {
      body.scope_id = payload.scope_id;
    }
    await http.post(`/hotel/${props.hotelId}/configuration/submit/clear-value/v1`, body);

    await refreshValues();
  } catch (err: any) {
    instance?.proxy?.$archNotify?.({
      color: "error",
      message: errorMessage(err, saveFailedText()),
    });
    emit("error", { message: errorMessage(err, "Failed to clear override") });
  }
};

onMounted(() => {
  fetchData();
});

watch(() => props.hotelId, () => {
  fetchData();
});

watch([() => props.scopeType, () => props.scopeId], () => {
  fetchData();
});

watch(() => props.categoryCodes, () => {
  fetchData();
}, { deep: true });
</script>

<style scoped>
.st-configuration-form {
  min-height: 100px;
}

.st-configuration-form__body {
  display: flex;
  gap: 16px;
  height: calc(100vh - 260px);
  min-height: 300px;
}

.st-configuration-form__sidebar {
  min-width: 200px;
  max-width: 240px;
  overflow-y: auto;
  flex-shrink: 0;
}

.st-configuration-form__content {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding-top: 8px;
  padding-right: 4px;
}
</style>
